import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://nieruchomy.pl';
const SITE_NAME = 'Nieruchomy.pl';
const DEFAULT_DESC = 'Nieruchomy.pl — darmowy portal ogłoszeń nieruchomości w Polsce. Mieszkania, domy, lokale. Sprzedaż i wynajem bez reklam.';
const DEFAULT_IMG = `${SITE_URL}/og-default.svg`;

export default function SEO({ title, description, image, url, schema, noindex = false }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Darmowy portal nieruchomości`;
  const desc = description || DEFAULT_DESC;
  const img = image || DEFAULT_IMG;
  const canonical = url || SITE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content="pl_PL" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

// Schema.org dla ogłoszenia nieruchomości
export function buildListingSchema(listing, siteUrl = SITE_URL) {
  const TYPE_MAP = { sprzedaz: 'ForSale', wynajem: 'ForRent', nowe: 'ForSale' };
  const mainImg = listing.images?.find(i => i.isMain)?.url || listing.images?.[0]?.url;
  const url = `${siteUrl}/ogloszenia/${listing.slug || listing.id}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: listing.title,
    description: listing.description || '',
    url,
    image: mainImg ? [mainImg] : [],
    datePosted: listing.createdAt,
    offers: {
      '@type': 'Offer',
      price: listing.price,
      priceCurrency: 'PLN',
      availability: 'https://schema.org/InStock',
      businessFunction: `https://schema.org/${TYPE_MAP[listing.type] || 'ForSale'}`,
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: listing.location,
      streetAddress: listing.address || listing.location,
      addressCountry: 'PL',
    },
    floorSize: {
      '@type': 'QuantitativeValue',
      value: listing.area,
      unitCode: 'MTK',
    },
    numberOfRooms: listing.rooms,
    leaseLength: listing.type === 'wynajem' ? { '@type': 'Duration', duration: 'P1M' } : undefined,
  };
}

// Schema.org dla strony głównej
export function buildSiteSchema(siteUrl = SITE_URL) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: siteUrl,
    description: DEFAULT_DESC,
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${siteUrl}/ogloszenia?city={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'pl',
  };
}

// Schema.org breadcrumb
export function buildBreadcrumbSchema(items, siteUrl = SITE_URL) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  };
}
